import { useTranslation } from "@coworker/locales";
import { NotificationTranslationKeyType } from "@fixa/notifications";
import { useFormatFieldValue } from "@coworker/apprestructured/src/task-management/hooks/useFormatFieldValue";
import { useFormatArrayFieldValue } from "@coworker/apprestructured/src/task-management/hooks/useFormatArrayFieldValue";
import type { FieldFormat } from "@coworker/apprestructured/src/task-management/hooks/useFormatFieldValue";

// Type used in the backend when building the notification's payload
type RawTranslationProp = {
  rawValue: unknown;
  format?: FieldFormat;
};

export function useNotificationString(
  notificationString: string | NotificationTranslationKeyType
): string {
  const { t } = useTranslation();
  const formatFieldValue = useFormatFieldValue();
  const formatArrayFieldValue = useFormatArrayFieldValue();

  if (typeof notificationString === "string") return notificationString;

  const { key, options } = notificationString;
  // The `translationProps` are extracted for special handling because they are raw
  // values that need to be formatted before being passed to the translation function.
  const { translationProps: rawTranslationProps, ...restOptions } = (options ??
    {}) as {
    translationProps?: Record<string, RawTranslationProp>;
  } & Record<string, unknown>;

  if (rawTranslationProps) {
    const resolvedProps: Record<string, string> = {};
    for (const [propKey, { rawValue, format }] of Object.entries(
      rawTranslationProps
    )) {
      resolvedProps[propKey] = Array.isArray(rawValue)
        ? formatArrayFieldValue(
            rawValue.map((value) => String(value ?? "")),
            format
          )
        : formatFieldValue(
            typeof rawValue === "string" ? rawValue : String(rawValue ?? ""),
            format
          );
    }

    // Resolved translation props are layered on top of any
    // other i18next options such as assigner or count props.
    return t(key, { ...restOptions, ...resolvedProps });
  }

  return t(key, options);
}
